import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RefreshCw, Eye, ChevronRight, Package, Filter, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, Clock, XCircle, AlertCircle, Briefcase, Workflow, X, ChevronDown, GitBranch, Maximize2, Plus, Layers, Trash2, Zap, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';
import UnifiedWorkflowEditor from '@/components/WorkflowTemplateManager';
import WorkflowDetailModalComponent from '@/components/workflow_modal';

// Propagation Modal Component
function PropagationModal({ isOpen, onClose, template, onPropagationComplete }) {
  const { authenticatedFetch } = useAuth();
  
  const [excludeCompleted, setExcludeCompleted] = useState(true);
  const [excludeIdentifiers, setExcludeIdentifiers] = useState('glasses_image_ingestion');
  const [includeIdentifiers, setIncludeIdentifiers] = useState('');
  const [targetWorkflowIds, setTargetWorkflowIds] = useState('');
  const [dryRun, setDryRun] = useState(true); // Force dry run by default
  const [isPropagating, setIsPropagating] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handlePropagate = async () => {
    setIsPropagating(true);
    setError('');
    setResults(null);

    try {
      const filters = {
        exclude_completed: excludeCompleted,
        exclude_identifiers: excludeIdentifiers
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0),
        include_identifiers: includeIdentifiers
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0) || null,
        target_workflow_ids: targetWorkflowIds
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0) || null
      };

      // Clean up null arrays
      if (!filters.include_identifiers || filters.include_identifiers.length === 0) {
        filters.include_identifiers = null;
      }
      if (!filters.target_workflow_ids || filters.target_workflow_ids.length === 0) {
        filters.target_workflow_ids = null;
      }

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/workflows2/template/${template.id}/propagate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            template_id: template.id,
            filters: filters,
            dry_run: dryRun
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Propagation failed: ${response.status}`);
      }

      const data = await response.json();
      setResults(data);

      if (!dryRun && onPropagationComplete) {
        onPropagationComplete();
      }
    } catch (err) {
      console.error('Error during propagation:', err);
      setError(err.message || 'Failed to propagate template');
    } finally {
      setIsPropagating(false);
    }
  };

  const handleClose = () => {
    if (!isPropagating) {
      setResults(null);
      setError('');
      setDryRun(true); // Reset to dry run
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Propagate Template Changes</h2>
              <p className="text-amber-100 text-sm mt-1">
                {template.name} (v{template.version})
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isPropagating}
            className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!results ? (
            <div className="space-y-6">
              {/* Dry Run Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-900 text-sm">Dry Run Mode Enabled</p>
                  <p className="text-amber-800 text-sm mt-1">
                    This will preview changes without applying them. Uncheck the dry run option below to apply changes for real.
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={excludeCompleted}
                      onChange={(e) => setExcludeCompleted(e.target.checked)}
                      disabled={isPropagating}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Exclude Completed Nodes (Safe Default)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Only update pending nodes. If unchecked, completed nodes will be reset to pending.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exclude Node Identifiers
                  </label>
                  <input
                    type="text"
                    value={excludeIdentifiers}
                    onChange={(e) => setExcludeIdentifiers(e.target.value)}
                    placeholder="node_type_1, node_type_2"
                    disabled={isPropagating}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-100 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated list of node identifiers to skip (e.g., glasses_image_ingestion)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Include Only Specific Identifiers (Optional)
                  </label>
                  <input
                    type="text"
                    value={includeIdentifiers}
                    onChange={(e) => setIncludeIdentifiers(e.target.value)}
                    placeholder="Leave empty to include all, or specify: node_type_1, node_type_2"
                    disabled={isPropagating}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-100 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If specified, only these node types will be updated
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Workflow IDs (Optional)
                  </label>
                  <input
                    type="text"
                    value={targetWorkflowIds}
                    onChange={(e) => setTargetWorkflowIds(e.target.value)}
                    placeholder="Leave empty for all workflows, or specify: workflow-id-1, workflow-id-2"
                    disabled={isPropagating}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-100 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Apply changes only to specific workflows
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dryRun}
                      onChange={(e) => setDryRun(e.target.checked)}
                      disabled={isPropagating}
                      className="w-4 h-4 text-amber-600 rounded focus:ring-2 focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Dry Run (Preview Changes Only)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Recommended: Preview changes before applying them
                  </p>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-800 text-sm font-semibold">Error</p>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-indigo-900 mb-4">
                  {results.dry_run ? 'Propagation Preview' : 'Propagation Results'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-indigo-600 font-medium uppercase">Total Workflows</p>
                    <p className="text-2xl font-bold text-indigo-900">{results.total_workflows_found}</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium uppercase">Processed</p>
                    <p className="text-2xl font-bold text-green-900">{results.workflows_processed}</p>
                  </div>
                  <div>
                    <p className="text-xs text-amber-600 font-medium uppercase">Nodes Updated</p>
                    <p className="text-2xl font-bold text-amber-900">{results.summary.total_nodes_updated}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600 font-medium uppercase">Nodes Reset</p>
                    <p className="text-2xl font-bold text-purple-900">{results.summary.total_nodes_reset}</p>
                  </div>
                </div>
                {results.dry_run && (
                  <div className="mt-4 bg-amber-100 border border-amber-300 rounded-lg p-3">
                    <p className="text-amber-800 text-sm font-medium">
                      ⚠️ This is a preview only. No changes have been applied.
                    </p>
                  </div>
                )}
              </div>

              {/* Per-Workflow Results */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Workflow Details</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {results.results.map((result, idx) => (
                    <div
                      key={result.workflow_id}
                      className={`border rounded-lg p-4 ${
                        result.errors.length > 0
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{result.workflow_name}</p>
                          <p className="text-xs text-gray-500 font-mono">{result.workflow_id}</p>
                        </div>
                        {result.errors.length > 0 ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <p className="text-gray-600">Nodes Updated</p>
                          <p className="font-semibold text-gray-900">{result.nodes_updated.length}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Nodes Skipped</p>
                          <p className="font-semibold text-gray-900">{result.nodes_skipped.length}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Nodes Reset</p>
                          <p className="font-semibold text-gray-900">{result.nodes_reset_to_pending.length}</p>
                        </div>
                      </div>

                      {result.errors.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <p className="text-xs font-semibold text-red-800 mb-1">Errors:</p>
                          {result.errors.map((err, errIdx) => (
                            <p key={errIdx} className="text-xs text-red-700">• {err}</p>
                          ))}
                        </div>
                      )}

                      {Object.keys(result.version_changes).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Version Changes:</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {Object.entries(result.version_changes).map(([nodeId, change]) => (
                              <div key={nodeId} className="text-xs bg-white rounded p-2 border border-gray-200">
                                <p className="font-medium text-gray-900">{change.node_name}</p>
                                <p className="text-gray-600">
                                  v{change.old_version} → v{change.new_version} | {change.old_status} → {change.new_status}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 p-6 flex items-center justify-between">
          {results ? (
            <>
              <button
                onClick={() => setResults(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Back to Settings
              </button>
              <div className="flex items-center gap-3">
                {results.dry_run && (
                  <button
                    onClick={() => {
                      setResults(null);
                      setDryRun(false);
                    }}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4" />
                    Apply Changes For Real
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                disabled={isPropagating}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handlePropagate}
                disabled={isPropagating}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 text-sm font-medium flex items-center gap-2"
              >
                {isPropagating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Propagating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    {dryRun ? 'Preview Changes' : 'Propagate Now'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal Component
function DeleteConfirmationModal({ isOpen, onClose, onConfirm, templateName, templateIdentifier }) {
  const [inputValue, setInputValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (inputValue !== templateIdentifier) {
      setError('Identifier does not match');
      return;
    }

    setIsDeleting(true);
    setError('');
    
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setInputValue('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Delete Template</h2>
              <p className="text-red-100 text-sm mt-1">
                This action cannot be undone
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-2">
              You are about to delete the template:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-gray-900">{templateName}</p>
              <p className="text-sm text-gray-600 mt-1">
                Identifier: <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">{templateIdentifier}</code>
              </p>
            </div>
            <p className="text-gray-700 mb-4">
              To confirm deletion, please type the template identifier:
            </p>
            <div>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setError('');
                }}
                placeholder="Enter template identifier"
                disabled={isDeleting}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:bg-gray-100"
                autoFocus
              />
              {error && (
                <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                  <XCircle className="w-4 h-4" />
                  {error}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={handleClose}
              disabled={isDeleting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isDeleting || inputValue !== templateIdentifier}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete Template
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Template Modal Component
function CreateTemplateModal({ isOpen, onClose, onRefresh }) {
  const handleSuccess = (result) => {
    console.log('Template created:', result);
    if (onRefresh) {
      onRefresh();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plus className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Create Template Workflow</h2>
              <p className="text-purple-100 text-sm mt-1">
                Build a reusable workflow template
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <UnifiedWorkflowEditor mode="create" onSuccess={handleSuccess} />
        </div>
      </div>
    </div>
  );
}

// Workflow Detail Modal Component
function WorkflowDetailModal({ isOpen, onClose, workflowId, isTemplate, onRefresh }) {
  const handleSuccess = (result) => {
    console.log('Template updated:', result);
    if (onRefresh) {
      onRefresh();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">
                {isTemplate ? 'Template' : 'Workflow'} Details
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                View and edit workflow
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <UnifiedWorkflowEditor 
            mode="view/update" 
            workflowUuid={workflowId} 
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>
  );
}

// Keep your existing WorkflowDetailModal component for templates:
function WorkflowEditorModal({ isOpen, onClose, workflowId, onRefresh }) {
  const handleSuccess = (result) => {
    console.log('Template updated:', result);
    if (onRefresh) {
      onRefresh();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Template Details</h2>
              <p className="text-blue-100 text-sm mt-1">
                View and edit workflow template
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <UnifiedWorkflowEditor 
            mode="view/update" 
            workflowUuid={workflowId} 
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>
  );
}

function WorkflowsTable() {
  const { authenticatedFetch } = useAuth();
  
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState('workflows');
  
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createTemplateModalOpen, setCreateTemplateModalOpen] = useState(false);
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const [propagationModalOpen, setPropagationModalOpen] = useState(false);
  const [templateToPropagate, setTemplateToPropagate] = useState(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState({
    status: [],
    identifier: [],
    name: '',
    kind: [],
    workflow_template_id: []
  });
  
  // Sort state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const observerTarget = useRef(null);
  const pageSize = 20;

  // Fetch filter options (only for workflows, not templates)
  const fetchFilterOptions = useCallback(async () => {
    if (viewMode === 'templates') return;
    
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/workflows2/workflows/filter-options`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFilterOptions(data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, [authenticatedFetch, viewMode]);

  useEffect(() => {
    if (viewMode === 'workflows') {
      fetchFilterOptions();
    }
  }, [viewMode, fetchFilterOptions]);

  const buildQueryParams = useCallback((page, mode) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });

    // Only add filters and sorting for workflows mode
    if (mode === 'workflows') {
      // Add sorting
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      // Add filters
      if (filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters.identifier.length > 0) {
        params.append('identifier', filters.identifier.join(','));
      }
      if (filters.name.trim()) {
        params.append('name', filters.name.trim());
      }
      if (filters.kind.length > 0) {
        params.append('kind', filters.kind.join(','));
      }
      if (filters.workflow_template_id.length > 0) {
        params.append('workflow_template_id', filters.workflow_template_id.join(','));
      }
    }

    return params.toString();
  }, [filters, sortBy, sortOrder]);

  const fetchWorkflows = useCallback(async (page, mode) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const endpoint = mode === 'templates' 
        ? `${config.BACKEND_API_ADDRESS}/workflows2/templates/list`
        : `${config.BACKEND_API_ADDRESS}/workflows2/workflows/list`;
      
      const queryParams = buildQueryParams(page, mode);
      const response = await authenticatedFetch(`${endpoint}?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (page === 1) {
        setWorkflows(data.items);
      } else {
        setWorkflows(prev => [...prev, ...data.items]);
      }
      
      setTotalCount(data.total);
      setHasMore(page < data.total_pages);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, loading, buildQueryParams]);

  useEffect(() => {
    setWorkflows([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchWorkflows(1, viewMode);
  }, [viewMode]);

  // Refresh when filters change (only for workflows)
  useEffect(() => {
    if (viewMode === 'workflows') {
      setWorkflows([]);
      setCurrentPage(1);
      setHasMore(true);
      fetchWorkflows(1, viewMode);
    }
  }, [filters, sortBy, sortOrder]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchWorkflows(currentPage + 1, viewMode);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage, viewMode, fetchWorkflows]);

  const handleRefresh = () => {
    setWorkflows([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchWorkflows(1, viewMode);
  };

  const handleViewDetails = (workflowId) => {
    setSelectedWorkflowId(workflowId);
    setDetailModalOpen(true);
  };

  const handleDeleteClick = (workflow) => {
    setTemplateToDelete(workflow);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/workflows2/template/${templateToDelete.id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to delete template: ${response.status}`);
      }

      handleRefresh();
      setDeleteModalOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error('Error deleting template:', err);
      throw err;
    }
  };

  const handlePropagateClick = (workflow) => {
    setTemplateToPropagate(workflow);
    setPropagationModalOpen(true);
  };

  const handlePropagationComplete = () => {
    handleRefresh();
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const toggleFilterValue = (filterType, value) => {
    setFilters(prev => {
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return {
        ...prev,
        [filterType]: newValues
      };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      status: [],
      identifier: [],
      name: '',
      kind: [],
      workflow_template_id: []
    });
  };
  
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default desc order
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  
  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-indigo-600" />
      : <ArrowDown className="w-4 h-4 text-indigo-600" />;
  };

  const hasActiveFilters = () => {
    return filters.status.length > 0 ||
           filters.identifier.length > 0 ||
           filters.name.trim() !== '' ||
           filters.kind.length > 0 ||
           filters.workflow_template_id.length > 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'template': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      case 'template': return <Layers className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {viewMode === 'templates' ? 'Workflow Templates' : 'Workflows'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} total {viewMode === 'templates' ? 'templates' : 'workflows'}
                {hasActiveFilters() && ' (filtered)'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {viewMode === 'workflows' && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    showFilters || hasActiveFilters()
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Filters {hasActiveFilters() && `(${Object.values(filters).flat().filter(v => v).length})`}
                  </span>
                </button>
              )}
              {viewMode === 'templates' && (
                <button
                  onClick={() => setCreateTemplateModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Create Template</span>
                </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => setViewMode('workflows')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'workflows'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Workflow className="w-4 h-4" />
                Workflows
              </div>
            </button>
            <button
              onClick={() => setViewMode('templates')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'templates'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Templates
              </div>
            </button>
          </div>

          {/* Filters Panel - Only for Workflows */}
          {viewMode === 'workflows' && showFilters && filterOptions && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
                {hasActiveFilters() && (
                  <button
                    onClick={clearAllFilters}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                  >
                    <X className="w-3 h-3" />
                    Clear all
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Name Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    placeholder="Search by name..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Status ({filters.status.length} selected)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.statuses.map(status => (
                      <button
                        key={status}
                        onClick={() => toggleFilterValue('status', status)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                          filters.status.includes(status)
                            ? getStatusColor(status)
                            : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Kind Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Kind ({filters.kind.length} selected)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.kinds.map(kind => (
                      <button
                        key={kind}
                        onClick={() => toggleFilterValue('kind', kind)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                          filters.kind.includes(kind)
                            ? 'bg-blue-100 text-blue-800 border-blue-200'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {kind}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Identifier Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Identifier ({filters.identifier.length} selected)
                  </label>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {filterOptions.identifiers.map(identifier => (
                      <label key={identifier} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={filters.identifier.includes(identifier)}
                          onChange={() => toggleFilterValue('identifier', identifier)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <code className="text-xs">{identifier}</code>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Template Filter */}
                {filterOptions.template_ids.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Template ({filters.workflow_template_id.length} selected)
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {filterOptions.template_ids.map(templateId => (
                        <label key={templateId} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={filters.workflow_template_id.includes(templateId)}
                            onChange={() => toggleFilterValue('workflow_template_id', templateId)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-xs font-mono truncate">{templateId}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading {viewMode}</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {workflows.length === 0 && !loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  {viewMode === 'templates' ? (
                    <Layers className="w-8 h-8 text-gray-400" />
                  ) : (
                    <Workflow className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  No {viewMode === 'templates' ? 'templates' : 'workflows'} found
                </h3>
                <p className="text-gray-600">
                  {hasActiveFilters() 
                    ? 'Try adjusting your filters'
                    : viewMode === 'templates' 
                      ? 'Create your first workflow template to get started'
                      : 'Start by creating some workflows'
                  }
                </p>
                {hasActiveFilters() && (
                  <button
                    onClick={clearAllFilters}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span className="text-sm font-medium">Clear Filters</span>
                  </button>
                )}
                {viewMode === 'templates' && !hasActiveFilters() && (
                  <button
                    onClick={() => setCreateTemplateModalOpen(true)}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Create Template</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('name')}
                          className="flex items-center gap-2 hover:text-gray-700 transition-colors group"
                          disabled={viewMode === 'templates'}
                        >
                          Name
                          {viewMode === 'workflows' && getSortIcon('name')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('identifier')}
                          className="flex items-center gap-2 hover:text-gray-700 transition-colors group"
                          disabled={viewMode === 'templates'}
                        >
                          Identifier
                          {viewMode === 'workflows' && getSortIcon('identifier')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('status')}
                          className="flex items-center gap-2 hover:text-gray-700 transition-colors group"
                          disabled={viewMode === 'templates'}
                        >
                          Status
                          {viewMode === 'workflows' && getSortIcon('status')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('kind')}
                          className="flex items-center gap-2 hover:text-gray-700 transition-colors group"
                          disabled={viewMode === 'templates'}
                        >
                          Kind
                          {viewMode === 'workflows' && getSortIcon('kind')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('version')}
                          className="flex items-center gap-2 hover:text-gray-700 transition-colors group"
                          disabled={viewMode === 'templates'}
                        >
                          Version
                          {viewMode === 'workflows' && getSortIcon('version')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nodes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('created_at')}
                          className="flex items-center gap-2 hover:text-gray-700 transition-colors group"
                          disabled={viewMode === 'templates'}
                        >
                          Created
                          {viewMode === 'workflows' && getSortIcon('created_at')}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {workflows.map((workflow) => (
                      <tr 
                        key={workflow.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {viewMode === 'templates' ? (
                              <Layers className="w-4 h-4 text-purple-600" />
                            ) : (
                              <Workflow className="w-4 h-4 text-indigo-600" />
                            )}
                            <span className="text-sm font-medium text-gray-900">
                              {workflow.name || 'Untitled'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {workflow.identifier || 'N/A'}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(workflow.status)}`}>
                            {getStatusIcon(workflow.status)}
                            {workflow.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            {workflow.kind}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          v{workflow.version}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <GitBranch className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{workflow.node_count || 0}</span>
                            <span className="text-gray-500">nodes</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {workflow.created_at ? new Date(workflow.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDetails(workflow.id)}
                              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs font-medium"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                            {viewMode === 'templates' && (
                              <>
                                <button
                                  onClick={() => handlePropagateClick(workflow)}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-xs font-medium"
                                >
                                  <Zap className="w-3 h-3" />
                                  Propagate
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(workflow)}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-medium"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div ref={observerTarget} className="px-6 py-4 text-center">
                {loading && (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-sm text-gray-600">Loading more...</span>
                  </div>
                )}
                {!hasMore && workflows.length > 0 && (
                  <span className="text-sm text-gray-500">
                    No more {viewMode === 'templates' ? 'templates' : 'workflows'} to load
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    <CreateTemplateModal
      isOpen={createTemplateModalOpen}
      onClose={() => setCreateTemplateModalOpen(false)}
      onRefresh={handleRefresh}
    />

    {/* Conditional rendering based on viewMode */}
    {viewMode === 'templates' ? (
      // Use the editor modal for templates
      <WorkflowEditorModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        workflowId={selectedWorkflowId}
        onRefresh={handleRefresh}
      />
    ) : (
      // Use the visualization modal for workflows
      <WorkflowDetailModalComponent
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        workflowId={selectedWorkflowId}
        isTemplate={false}
      />
    )}

    <DeleteConfirmationModal
      isOpen={deleteModalOpen}
      onClose={() => {
        setDeleteModalOpen(false);
        setTemplateToDelete(null);
      }}
      onConfirm={handleDeleteConfirm}
      templateName={templateToDelete?.name || 'Untitled'}
      templateIdentifier={templateToDelete?.identifier || ''}
    />

    <PropagationModal
      isOpen={propagationModalOpen}
      onClose={() => {
        setPropagationModalOpen(false);
        setTemplateToPropagate(null);
      }}
      template={templateToPropagate}
      onPropagationComplete={handlePropagationComplete}
    />
  </div>
);
}


export default WorkflowsTable;